/**
 * `ImportedModelLoadTransaction`
 *
 * 导入模型加载链路的 loader-facing 事务对象。职责：
 *   - 所有"新生成的 PartAsset / AssemblyModelAsset"先被 stage 到事务内部结构，
 *     不直接写入共享 `PartAssetCache` / `AssemblyModelCache`；
 *   - `commit()` 成功后，把 staged 资产一次性写入共享 cache，然后清空 staging；
 *   - `abort()` / `rollback()` 负责 dispose 掉 staged 资产里持有的 three.js 资源
 *     （geometry / material / textures）并清空 staging；
 *   - 生命周期方法（commit / abort / rollback）幂等；
 *   - `status` 与 `isAborted` 作为 loader 中断信号的唯一来源（宿主 seq guard 不再直接参与）。
 *
 * 设计说明：
 *   - `abort()` 会自动触发 `rollback()`；其余场景（例如 loader 检测到 guard stale 后
 *     主动回滚）也可直接调用 `rollback()`，随后手动 `abort()` 把状态固化。
 *   - `commit()` 之后事务已经把资源所有权转交给共享 cache，后续 `abort()` / `rollback()`
 *     一律视为 no-op，避免把共享 cache 中其他 in-flight request 依赖的资源误 dispose。
 *   - 共享 cache 的 scope 写入策略由构造参数 `publishScopeKey` 决定，默认 project。
 */

import {
  type AssemblyModelAsset,
  type AssemblyModelCache,
  DEFAULT_PARSED_GLB_CACHE_VARIANT,
  type ParsedGlbCache,
  type PartAsset,
  type PartAssetCache,
  disposeAssemblyModelAssetResources,
  disposePartAsset,
} from '@puppycad/cad-core'

import {
  DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE,
  type ImportedModelPublishScopeKey,
} from './imported-model-load-scope'

export type ImportedModelLoadTransactionStatus = 'active' | 'committed' | 'aborted'

export interface ImportedModelLoadTransactionOptions {
  /** 共享 PartAsset cache；commit 的写入目标 */
  partAssetCache: PartAssetCache
  /** 共享 AssemblyModel cache；commit 的写入目标 */
  assemblyModelCache: AssemblyModelCache
  /**
   * 共享 ParsedGlbCache；事务通过 `trackParsedArtifact` 记录"本次加载触碰过的
   * GLTF 条目"，在 commit / rollback 两个终态都会主动 evict——把 geometry/material
   * 的所有权转交给 staged PartAsset / AssemblyModelAsset（commit 场景）或让下一次
   * 加载重新下载而不是读到已 dispose 的 GLTF（rollback 场景）。
   */
  parsedGlbCache: ParsedGlbCache
  /** 该事务 commit 时写入 cache 的 scope，默认 project */
  publishScopeKey?: ImportedModelPublishScopeKey
}

interface StagedPartEntry {
  asset: PartAsset
  scopeKey: ImportedModelPublishScopeKey
}

interface StagedAssemblyEntry {
  asset: AssemblyModelAsset
  scopeKey: ImportedModelPublishScopeKey
}

export class ImportedModelLoadTransaction {
  private readonly partAssetCache: PartAssetCache
  private readonly assemblyModelCache: AssemblyModelCache
  private readonly parsedGlbCache: ParsedGlbCache
  private readonly defaultScopeKey: ImportedModelPublishScopeKey

  private readonly stagedPartAssets: StagedPartEntry[] = []
  private readonly stagedAssemblyAssets: StagedAssemblyEntry[] = []
  /**
   * 本事务触碰过的 parsedGlbCache 条目集合。
   *
   * 设计说明：
   * 1. 使用 `Map<uuid, Map<version, Set<variant>>>` 保存结构化 key，避免字符串拆分歧义；
   * 2. 终态（commit 或 rollback）时统一 evict，避免 parsedGlbCache 里的 GLTF 与 staged
   *    资产/已 dispose 资源之间产生悬挂的共享 geometry/material 引用（bug C）。
   */
  private readonly trackedParsedArtifacts = new Map<string, Map<string, Set<string>>>()

  private _status: ImportedModelLoadTransactionStatus = 'active'

  constructor(options: ImportedModelLoadTransactionOptions) {
    this.partAssetCache = options.partAssetCache
    this.assemblyModelCache = options.assemblyModelCache
    this.parsedGlbCache = options.parsedGlbCache
    this.defaultScopeKey = options.publishScopeKey ?? DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE
  }

  /**
   * 登记一个与本事务绑定的 parsedGlbCache 条目。commit 或 rollback 时统一 evict。
   * `status != 'active'` 时为 no-op（不能把 cache 条目挂到已终态的事务上）。
   */
  trackParsedArtifact(
    fileUuid: string,
    version: string,
    variant: string = DEFAULT_PARSED_GLB_CACHE_VARIANT,
  ): void {
    if (this._status !== 'active') {
      return
    }
    const versions = this.trackedParsedArtifacts.get(fileUuid) ?? new Map<string, Set<string>>()
    const variants = versions.get(version) ?? new Set<string>()
    variants.add(variant)
    versions.set(version, variants)
    this.trackedParsedArtifacts.set(fileUuid, versions)
  }

  /**
   * 显式把某个 key 从 tracked set 中移除。用于外部 eager eviction 的场景（典型：
   * `assembly-loader.ts` 在 stagePartAsset 后立刻把对应 parsedGlbCache 条目删掉，
   * 以便把 GLTF 所有权立即交给 staged PartAsset）。
   *
   * 即使不调用此方法也不会产生 bug：后续 commit / rollback 再次 `.delete(uuid, version)`
   * 是幂等 no-op；但显式 untrack 可以让 trackedParsedArtifacts 更能准确反映事务还
   * "认为自己持有" 的 parsedGlbCache 条目。
   */
  untrackParsedArtifact(
    fileUuid: string,
    version: string,
    variant: string = DEFAULT_PARSED_GLB_CACHE_VARIANT,
  ): void {
    const versions = this.trackedParsedArtifacts.get(fileUuid)
    if (!versions) {
      return
    }
    const variants = versions.get(version)
    if (!variants) {
      return
    }
    variants.delete(variant)
    if (variants.size === 0) {
      versions.delete(version)
    }
    if (versions.size === 0) {
      this.trackedParsedArtifacts.delete(fileUuid)
    }
  }

  private evictTrackedParsedArtifacts(): void {
    this.trackedParsedArtifacts.forEach((versions, uuid) => {
      versions.forEach((variants, version) => {
        variants.forEach((variant) => {
          this.parsedGlbCache.delete(uuid, version, variant)
        })
      })
    })
    this.trackedParsedArtifacts.clear()
  }

  get status(): ImportedModelLoadTransactionStatus {
    return this._status
  }

  /** loader 中断信号的唯一来源（true 表示 loader 必须停止继续写资源）。 */
  get isAborted(): boolean {
    return this._status === 'aborted'
  }

  /** 该事务 commit 时写入 cache 的默认 scope key。 */
  get publishScopeKey(): ImportedModelPublishScopeKey {
    return this.defaultScopeKey
  }

  /**
   * Stage 一个新生成的 PartAsset，commit 时写入共享 PartAssetCache。
   * 对已 committed / aborted 的事务调用是 no-op（忽略）。
   */
  stagePartAsset(
    asset: PartAsset,
    scopeKey: ImportedModelPublishScopeKey = this.defaultScopeKey,
  ): void {
    if (this._status !== 'active') {
      return
    }
    this.stagedPartAssets.push({ asset, scopeKey })
  }

  /**
   * 查询已经 stage（但尚未 commit）的 AssemblyModelAsset。loader 在同一事务内的
   * 嵌套流程如果需要判断"子 assembly 资产是否已经被 stage"（此时共享 cache 还没
   * 写入），只能通过本方法检索 staging 视图，不能用 `assemblyModelCache.peek()` 读到。
   *
   * 按 (fileUuid, version) 精确匹配；当同 uuid+version 有多次 stage 时，返回最
   * 后一次（与 commit 时后写入覆盖前者的语义一致）。
   */
  peekStagedAssembly(fileUuid: string, version: string): AssemblyModelAsset | undefined {
    for (let i = this.stagedAssemblyAssets.length - 1; i >= 0; i -= 1) {
      const entry = this.stagedAssemblyAssets[i]
      if (entry.asset.fileUuid === fileUuid && entry.asset.version === version) {
        return entry.asset
      }
    }
    return undefined
  }

  /**
   * Stage 一个新生成的 AssemblyModelAsset，commit 时写入共享 AssemblyModelCache。
   * 对已 committed / aborted 的事务调用是 no-op（忽略）。
   *
   * **重要契约（调用方必读）：**
   * 本方法只 stage 该 assembly 本身的 `sceneTemplate` 以供 GPU 资源回滚使用。
   * 如果 asset 内还引用嵌入式 part 或嵌套 sub-assembly（即 `partAssetsByUuid` /
   * `assemblyAssetsByUuid` 非空），**调用方必须为每一个嵌入 part / 嵌套 assembly
   * 单独调用 `stagePartAsset` / `stageAssemblyAsset`**。
   *
   * rollback 不会自行遍历 `partAssetsByUuid` / `assemblyAssetsByUuid`——这是刻意
   * 设计，目的在于避免 double-dispose 已经写入共享 cache、可能被其他 in-flight
   * request 复用的资产（典型：依赖解析时命中 cache 的 PartAsset）。transaction
   * 的 "staging 所有权" 粒度 == 单个 stageXxxAsset 调用，由调用方显式声明。
   */
  stageAssemblyAsset(
    asset: AssemblyModelAsset,
    scopeKey: ImportedModelPublishScopeKey = this.defaultScopeKey,
  ): void {
    if (this._status !== 'active') {
      return
    }
    this.stagedAssemblyAssets.push({ asset, scopeKey })
  }

  /**
   * 把所有 staged 资产一次性写入共享 cache。幂等——重复调用不会再次写入。
   * 已 abort 的事务调用 commit 为 no-op。
   *
   * **订阅保留不变量（subscription preservation invariant）：**
   *   - `commit()` 通过对共享 cache 逐项调用 `.set()` 来发布 staged 资产；
   *   - `PartAssetCache` / `AssemblyModelCache` 的 `.set()` 每次调用都会 fire 一次订阅
   *     回调（subscribe listener），因此每个 staged 资产在 commit 后**恰好触发一次**
   *     订阅通知；
   *   - 相对地，`abort()` / `rollback()` 只走 dispose 路径，不调用任何 `.set()`，因此
   *     staged 资产不会对订阅者可见，订阅 0 次触发。
   *   - 这条不变量是 `useEditorGlbLoader.syncPartTabsWithImportedModelCaches()` 之所以
   *     能等到 commit 完成才回填 tab 类型/图标的依赖。测试在
   *     `imported-model-assembly-transaction.test.ts` 中通过 subscribe mock 计数锁住。
   */
  commit(): void {
    if (this._status !== 'active') {
      return
    }
    for (const entry of this.stagedPartAssets) {
      this.partAssetCache.set(entry.asset, entry.scopeKey)
    }
    for (const entry of this.stagedAssemblyAssets) {
      this.assemblyModelCache.set(entry.asset, entry.scopeKey)
    }
    // commit 成功后 staged 资产正式接管 geometry/material 的所有权；parsedGlbCache
    // 再继续持有同一组共享资源是 latent corruption source（owning cache 清理时会
    // 连带把 parsedGlbCache 里的 GLTF 弄成"指向已 dispose 资源"）。evict 做所有权
    // 切换，让后续如果还需要原始 GLTF 重新下载即可。
    this.evictTrackedParsedArtifacts()
    this.clearStaging()
    this._status = 'committed'
  }

  /**
   * 触发事务中止。幂等。
   * 对仍 active 的事务：固化状态 → 'aborted'，同步触发 `rollback()`。
   * 对 committed 的事务：no-op（资源所有权已转交 cache）。
   * 对 aborted 的事务：no-op。
   */
  abort(): void {
    if (this._status !== 'active') {
      return
    }
    this._status = 'aborted'
    this.runRollback()
  }

  /**
   * 显式触发回滚：dispose staged 三维资源并清空 staging。幂等。
   * 对 committed 的事务为 no-op；对 aborted 的事务，首次 abort 已跑过一次
   * rollback，再次调用也不会重复 dispose（staging 已空）。
   */
  rollback(): void {
    if (this._status === 'committed') {
      return
    }
    this.runRollback()
  }

  private runRollback(): void {
    // dispose part three.js 资源
    for (const entry of this.stagedPartAssets) {
      disposePartAsset(entry.asset)
    }
    // dispose assembly staged three.js 资源（sceneTemplate）
    for (const entry of this.stagedAssemblyAssets) {
      disposeAssemblyModelAssetResources(entry.asset)
    }
    // rollback 已 dispose 了 geometry/material；parsedGlbCache 里共享同一 geometry 的
    // GLTF 条目此时已经"内存 corrupted"，必须 evict，否则下一次命中 cache 会把已
    // dispose 的资源再次当成有效 GLTF 使用（render 黑 / 空 mesh 的 bug C 根因）。
    this.evictTrackedParsedArtifacts()
    this.clearStaging()
  }

  private clearStaging(): void {
    this.stagedPartAssets.length = 0
    this.stagedAssemblyAssets.length = 0
  }
}
